import { Body, Heading, IconButton } from '@assembly-js/design-system';
import { MenuItem } from '@mui/material';
import { FieldArrayRenderProps, FormikValues, useFormikContext } from 'formik';
import { useTranslation } from 'react-i18next';
import z from 'zod';

import { BaseTextField } from 'src/legacy/components/TextField';
import { useGetPortalConfigQuery } from 'src/services/api';
import { useCustomFields } from 'src/services/api/customFieldsApi';
import { CustomFieldEntityType } from 'src/store/clients/types';

// id, key, name, order, type
// don't include multi-select
const CLIENT_CORE_PARAM_OPTIONS = [
  { label: 'ID', value: 'client.id' },
  { label: 'Email', value: 'client.email' },
  { label: 'Given name', value: 'client.givenName' },
  { label: 'Family name', value: 'client.familyName' },
];

const COMPANY_CORE_PARAM_OPTIONS = [
  { label: 'ID', value: 'company.id' },
  { label: 'Name', value: 'company.name' },
];

export function URLQueryParamFormField<Values extends FormikValues>({
  replace,
  remove,
}: Pick<FieldArrayRenderProps, 'replace' | 'remove'>) {
  const { t } = useTranslation();
  // TODO(@foley): Should these be props?
  const { values, handleBlur } = useFormikContext<Values>();

  const clientCustomFieldsMap = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFieldsMap = useCustomFields(CustomFieldEntityType.COMPANY);
  const { data: portalConfig } = useGetPortalConfigQuery();
  const clientCustomFieldParamOptions = Object.values(clientCustomFieldsMap)
    .filter(({ type }) => type !== 'address')
    .map((field) => {
      return {
        label: field.name,
        value: `client.${field.id}`,
      };
    });
  const companyCustomFieldParamOptions = Object.values(companyCustomFieldsMap)
    .filter(({ type }) => type !== 'address')
    .map((field) => {
      return {
        label: field.name,
        value: `company.${field.id}`,
      };
    });
  const clientParamOptions = CLIENT_CORE_PARAM_OPTIONS;
  return (
    <>
      {(
        (values.queryParams ?? [{ key: '', value: '' }]) as Record<
          string,
          string
        >[]
      ).map(({ key, value }, index) => {
        return (
          <div key={`queryParams-${index}`} className="flex gap-2 mb-3">
            <BaseTextField
              className="flex-1 max-w-[256px]"
              fullWidth
              label={
                <div className="flex items-center">
                  <Body size="sm">URL parameter</Body>
                </div>
              }
              onBlur={handleBlur}
              onChange={(e) => {
                replace(index, {
                  key: e.target.value,
                  value,
                });
              }}
              name="queryParams.key"
              sizeVariant="medium"
              value={key}
              variant="outlined"
            />
            <BaseTextField
              className="flex-1  max-w-[256px]"
              fullWidth
              label={
                <div className="flex items-center">
                  <Body size="sm">Property</Body>
                </div>
              }
              name="queryParams.value"
              onBlur={handleBlur}
              onChange={(e) => {
                replace(index, {
                  key,
                  value: e.target.value,
                });
              }}
              select
              sizeVariant="medium"
              value={value}
              variant="outlined"
            >
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.client, { count: 1 })}
                </Heading>
              </MenuItem>
              {clientParamOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
              <MenuItem disabled style={{ opacity: 1 }}>
                <Heading size="xs" className="text-gray-small">
                  {t(($) => $.page_apps.client_custom_fields)}
                </Heading>
              </MenuItem>
              {clientCustomFieldParamOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
              {portalConfig?.structFields.disableCompanies === false && (
                <MenuItem disabled style={{ opacity: 1 }}>
                  <Heading size="xs" className="text-gray-small">
                    {t(($) => $.company, { count: 1 })}
                  </Heading>
                </MenuItem>
              )}
              {portalConfig?.structFields.disableCompanies === false &&
                COMPANY_CORE_PARAM_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              {portalConfig?.structFields.disableCompanies === false && (
                <MenuItem disabled style={{ opacity: 1 }}>
                  <Heading size="xs" className="text-gray-small">
                    {t(($) => $.page_apps.company_custom_fields)}
                  </Heading>
                </MenuItem>
              )}
              {portalConfig?.structFields.disableCompanies === false &&
                companyCustomFieldParamOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </BaseTextField>
            <IconButton
              icon="Close"
              onClick={(e) => {
                e.stopPropagation();
                remove(index);
              }}
              label="Remove parameter"
              variant="minimal"
              className="self-end mb-[2px] cursor-pointer flex-0"
            />
          </div>
        );
      })}
    </>
  );
}

export function URLQueryParamsPreview({
  queryParams,
  content,
}: {
  queryParams?: Record<string, string>[];
  content: string;
}) {
  const clientCustomFieldsMap = useCustomFields(CustomFieldEntityType.CLIENT);
  const companyCustomFieldsMap = useCustomFields(CustomFieldEntityType.COMPANY);
  if (!z.string().url().safeParse(content).success) return null;
  if (!queryParams || !queryParams.length) return null;
  const url = new URL(content);
  queryParams.forEach(({ key, value }) => {
    let [, label] = value.split('.');
    if (z.string().uuid().safeParse(label).success) {
      label =
        clientCustomFieldsMap[label]?.name ||
        companyCustomFieldsMap[label]?.name;
    }
    label = '{' + label + '}';
    url.searchParams.set(key, label);
  });
  const [urlWithoutQuery, queryString] = url.toString().split('?');
  return (
    <div className="bg-gray-100 p-3 overflow-scroll whitespace-pre-wrap">
      <Body tag="span">{decodeURIComponent(urlWithoutQuery)}</Body>
      <Body tag="span" className="text-gray-500 font-bold">
        {`?${decodeURIComponent(queryString)}`}
      </Body>
    </div>
  );
}
